import type { CanvasNote } from '@/src/renderer/lib/db/models'
import type { CanvasPanelState } from '../ProjectCanvas.types'
import { computeStickyFontSize, getNoteLineHeight } from './canvasNoteStyle'

/**
 * Wie viele Elemente der **Canvas-Anriss** zeigt.
 *
 * Zwei, nicht alle: Der Streifen ist 77px breit, und was hineinragt, soll
 * „hier liegt etwas" sagen — kein Inhaltsverzeichnis. Sie liegen zudem unter
 * der **Werkzeug-Pille**, also in der unteren Hälfte; ab dem dritten Zettel
 * läge in einem gewöhnlichen Fenster nur noch Papier auf Papier, und der
 * unterste fiele ohnehin aus dem Anschnitt.
 */
export const CANVAS_PEEK_NOTE_COUNT = 2

/**
 * Die Breite des Streifens in Pixeln. Im Figma gesetzt (48px Pille + 2 × 14,5px),
 * nicht hergeleitet. Dieselbe Zahl steht als `$peek-width` in `CanvasPeek.scss`
 * und als `--peek`/`--closing-to-peek` in `ProjectCanvasPanel.scss` — ein
 * Invarianten-Test hält sie zusammen (`canvasMotionInvariants.test.ts`).
 *
 * Gerechnet wird sie beim Zug am **Drag Handle**: Aus dem Anriss heraus folgt
 * der Canvas der Hand ab dieser Breite, nach rechts bis auf sie hinunter
 * (`trackSplitDrag`, `floor`).
 */
export const CANVAS_PEEK_WIDTH = 77

/**
 * Die Kantenlänge der **Notiz**-Miniatur im Anriss. Dieselbe Zahl steht als
 * `$peek-sticky-size` in `CanvasPeek.scss` — ein Invarianten-Test hält sie
 * zusammen (`CanvasPeek.test.tsx`).
 *
 * Sie ist nicht die echte Größe (168), sondern so klein, dass zwei Zettel
 * übereinander noch als einzelne lesbar sind. Aus dem Verhältnis der beiden
 * entsteht die Schriftgröße im Streifen (`peekStickyFontSize`).
 */
export const CANVAS_PEEK_STICKY_SIZE = 128

/**
 * Die Schriftgröße einer **Notiz** im **Anriss** — gerechnet wie auf dem
 * Canvas, nur für die Miniatur.
 *
 * Die Notiz rechnet ihre Schrift aus ihrem Text (`computeStickyFontSize`,
 * Miro-Art: wenig Text wird groß). Der Anriss trug dafür bis 2026-09-11 eine
 * feste Größe von 14px, und das brach sein Versprechen: „Hello" steht auf dem
 * Canvas groß da und lag im Streifen als Krümel (gemeldet 2026-09-11).
 *
 * **Dieselbe Funktion mit den Maßen der Miniatur** — und nicht die Größe des
 * echten Zettels heruntergerechnet. Zwei Gründe: Die Miniatur ist ein Quadrat,
 * gleich wie der Zettel liegt, also gibt ein Maßstab aus seiner Breite bei
 * einem liegenden Zettel zu viel Höhe her; und die Rechnung sorgt hier wie dort
 * selbst dafür, dass der Text ins Papier passt. Ein heruntergerechneter Wert
 * könnte über die Kante laufen, und der Zettel im Anriss trägt bewusst kein
 * `overflow: hidden` (dort ragt die Schatten-Textur hinaus).
 *
 * Der **Autor** spielt keine Rolle: Die Miniatur zeigt keinen, ihm muss also
 * auch keine Höhe abgehen.
 *
 * @example
 * // „Hello" auf einem Vorgabe-Zettel: 37px auf dem Canvas, 26px im Anriss
 * // (vorher dort 14px — dieselbe Notiz, zwei Bilder).
 * peekStickyFontSize(note)   // 26
 */
export function peekStickyFontSize(note: CanvasNote): number {
  return computeStickyFontSize(
    note.content,
    CANVAS_PEEK_STICKY_SIZE,
    CANVAS_PEEK_STICKY_SIZE,
    { fontKey: note.font },
  )
}

/**
 * Die Zeilenhöhe dazu — als Faktor, wie auf dem Canvas. Sie hängt an der
 * Schrift: Caveat und Permanent Marker bringen verschiedene Höhen mit, und
 * eine feste Zeilenhöhe (der Streifen trug 20px) zöge die Miniatur je Schrift
 * anders auseinander.
 */
export function peekStickyLineHeight(note: CanvasNote): number {
  return getNoteLineHeight(note.font)
}

/**
 * Wie lange die Zettel beim Zuziehen in den Streifen einfahren — gemessen ab
 * dem BEGINN des Zuziehens — und wie lange sie dabei nicht auf den Zeiger
 * reagieren (`canvas-peek--entering`).
 *
 * Sie kommen schon, während die Zettel des Canvas ausblenden (ab 0,15 s), und
 * brauchen die Dauer des Zuziehens (0,25 s): Muss `$peek-enter-delay +
 * $peek-enter` in `CanvasPeek.scss` sein (Invariante in `CanvasPeek.test.tsx`).
 * Ein Zeitgeber statt `animationend`: Bei `prefers-reduced-motion` läuft keine
 * Animation, und ein Ende, das nie kommt, ließe die Sperre hängen.
 */
export const CANVAS_PEEK_ENTER_MS = 400

/**
 * Die Elemente für den **Canvas-Anriss** — zuletzt bearbeitet zuerst.
 *
 * **Dekorativ, kein Ausschnitt** (Entscheidung mit Sven, 2026-09-02): Der
 * Anriss zeigt nicht, was am rechten Rand des gemerkten Ausschnitts liegt,
 * sondern das, woran zuletzt gearbeitet wurde. Ein echter Ausschnitt könnte
 * leer sein, obwohl der Canvas voll ist — und dann sagte der Streifen das
 * Gegenteil dessen, wofür er da ist.
 *
 * Nach `updatedAt`, nicht `lastModified`: `updatedAt` ist der fachliche
 * Stempel, `lastModified` der der Sync-Buchführung.
 *
 * @example
 * pickPeekNotes(notes)   // → [neueste, zweitneueste, drittneueste]
 */
export function pickPeekNotes(notes: CanvasNote[]): CanvasNote[] {
  return notes
    .filter(hasPeekContent)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
    .slice(0, CANVAS_PEEK_NOTE_COUNT)
}

/**
 * Ob ein Element im **Anriss** überhaupt etwas zu zeigen hat.
 *
 * **Nur Zettel mit Text** (Sven, 2026-09-07): Ein leerer Zettel im Streifen ist
 * ein Farbfleck — er sagt „hier liegt etwas", zeigt davon aber nichts. Und ein
 * **Bereich** ist kein Zettel; er trägt im Anriss ohnehin keine Darstellung.
 *
 * Bei der **Memo** zählt auch der Titel: Sie kann eine Überschrift ohne Text
 * tragen, und die ist Inhalt.
 *
 * Der Streifen SELBST fragt dasselbe (`shouldShowCanvasPeek`) — täte er es
 * nicht, stünde bei einem Canvas voller leerer Zettel ein Streifen da, in dem
 * nichts liegt.
 */
function hasPeekContent(note: CanvasNote): boolean {
  // Der Anriss zeigt ZETTEL — der **Text** ist keiner (CONTEXT.md, Text).
  if (note.kind === 'area' || note.kind === 'text') return false
  const text = `${note.title ?? ''}${note.content ?? ''}`
  return text.trim().length > 0
}

/**
 * Ob der **Canvas-Anriss** steht: nur **geschlossen** und nur mit Elementen.
 *
 * `undefined` heißt „lädt noch" und zählt wie leer — sonst spränge beim
 * Start erst die Pille und dann der Streifen ins Bild.
 *
 * Wer hier den GEZEIGTEN Zustand (`panelState`) übergibt und nicht die
 * Absicht, bekommt den Anriss erst, wenn der Canvas wirklich zu ist — nicht
 * schon während er zuläuft.
 */
export function shouldShowCanvasPeek(
  state: CanvasPanelState,
  notes: CanvasNote[] | undefined,
): boolean {
  // Dieselbe Frage wie `pickPeekNotes`: Ein **Bereich** ist kein Zettel, und
  // ein leerer Zettel hat nichts zu zeigen. Zwei verschiedene Antworten hier
  // und dort ergäben einen Streifen, in dem nichts liegt.
  return state === 'closed' && notes !== undefined && notes.some(hasPeekContent)
}
